import { isLang } from '@2l/locale'
import { eql, when } from '@2l/utils'
import * as Sentry from '@sentry/vue'
import ky from 'ky'

import { STORAGE_KEYS } from '@/constants/storageKeys'

const resource = new URL(window.location.href)
const developmentId =
  __DEVELOPMENT_SESSION_ID__ || localStorage.getItem('developmentId')

const common: Record<string, string> = {
  Accept: 'application/json',
  'X-Platform-Id': '255c5893-e2a4-4860-83d8-c2e30433e9f6',
  'X-Requested-With': 'XMLHttpRequest'
}

if (developmentId) {
  common['X-Session-Id'] = developmentId
}

const API_URL = resource.searchParams.get('api_url') || __API_URL__

const isUnauthorized = eql('response.status', 401)
const accessForbidden = when({
  'response.status': 403,
  'response.data.code': [
    'trial_used',
    'trial_expired',
    'payment_required',
    'bill_expired'
  ]
})

interface RequestHeadersData {
  acceptLanguage: string
  xSystemLanguage: string
  currency: string
}

interface ApiClientContext {
  getRequestHeadersData: () => Partial<RequestHeadersData>
  onUnauthorized: (error: unknown) => Promise<unknown> | void
  onAccessForbidden: (error: unknown) => Promise<unknown> | void
}

const apiContext: ApiClientContext = {
  getRequestHeadersData: () => ({}),
  onUnauthorized: () => {},
  onAccessForbidden: () => {}
}

export function setApiContext(ctx: ApiClientContext) {
  Object.assign(apiContext, ctx)
}

const DEFAULT_LANG = 'en'

export const apiClient = ky.create({
  prefixUrl: API_URL,
  credentials: 'include',
  headers: common,
  parseJson: (text) => (text ? JSON.parse(text) : null),
  hooks: {
    beforeRequest: [
      (request) => {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || ''
        request.headers.set('X-Session-Id', token)

        const ctx = apiContext.getRequestHeadersData()

        const acceptLanguage = ctx.acceptLanguage ?? DEFAULT_LANG
        const xSystemLanguage = ctx.xSystemLanguage ?? DEFAULT_LANG

        // activeProfile as Accept-Language for support languageLevel updates (see answer.js in server project)
        request.headers.set(
          'Accept-Language',
          isLang(acceptLanguage) ? acceptLanguage : DEFAULT_LANG
        )
        request.headers.set(
          'X-System-Language',
          isLang(xSystemLanguage) ? xSystemLanguage : DEFAULT_LANG
        )

        if (ctx.currency) {
          request.headers.set('X-Accept-Currency', ctx.currency)
        }
      }
    ],
    beforeError: [
      async (error) => {
        if (isUnauthorized(error)) {
          await apiContext.onUnauthorized(error)
        }
        if (accessForbidden(error)) {
          await apiContext.onAccessForbidden(error)
        }
        return error
      }
    ]
  }
})

window.addEventListener(
  'EWA.ANALYTICS:initialized',
  (event: any) => onInitializeTracker(event.detail.tracker),
  { once: true }
)

if ((window as any).EWA) {
  onInitializeTracker((window as any).EWA.ANALYTICS)
}

function onInitializeTracker(tracker) {
  if (!tracker) {
    return
  }

  tracker
    .getHeaders()
    .then((headers) => Object.assign(common, headers))
    .catch(Sentry.captureException)
}
